import React, { useState } from 'react';
import { Avatar } from 'antd';
import { UserOutlined } from '@ant-design/icons';
import {
  Card,
  HeaderActionSlot,
  List,
  MetaLine,
  Name,
  Row,
  SideHead,
  SideTitle,
  Subtitle,
  Text,
  TitleBlock,
  ToggleButton,
} from './peopleListCard.styled';

export type PeopleListCardItem = {
  id: string;
  name: string;
  subtitle?: string;
  avatarUrl?: string;
};

export interface PeopleListCardProps {
  title: string;
  /** Shown under the title (e.g. “12 members”) */
  meta?: string;
  items: PeopleListCardItem[];
  /** Rows shown while collapsed; the rest sit behind a “Show more” toggle */
  maxItems?: number;
  /** Right side of header (e.g. “See all”) */
  headerAction?: React.ReactNode;
  avatarSize?: number;
}

const PeopleListCard = ({
  title,
  meta,
  items,
  maxItems,
  headerAction,
  avatarSize = 34,
}: PeopleListCardProps) => {
  const [expanded, setExpanded] = useState(false);

  const isCollapsible = typeof maxItems === 'number' && items.length > maxItems;
  const rows = isCollapsible && !expanded ? items.slice(0, maxItems) : items;

  const hasRichHeader = Boolean(meta || headerAction);

  return (
    <Card>
      {hasRichHeader ? (
        <SideHead>
          <TitleBlock>
            <SideTitle>{title}</SideTitle>
            {meta ? <MetaLine>{meta}</MetaLine> : null}
          </TitleBlock>
          {headerAction ? <HeaderActionSlot>{headerAction}</HeaderActionSlot> : null}
        </SideHead>
      ) : (
        <SideTitle>{title}</SideTitle>
      )}

      <List>
        {rows.map((person) => (
          <Row key={person.id}>
            <Avatar src={person.avatarUrl} icon={<UserOutlined />} size={avatarSize} />
            <Text>
              <Name>{person.name}</Name>
              {person.subtitle ? <Subtitle>{person.subtitle}</Subtitle> : null}
            </Text>
          </Row>
        ))}
      </List>

      {isCollapsible ? (
        <ToggleButton type="button" onClick={() => setExpanded((open) => !open)}>
          {expanded ? 'Show less' : `Show more (${items.length - (maxItems as number)})`}
        </ToggleButton>
      ) : null}
    </Card>
  );
};

export default PeopleListCard;
